// Water events: timed pump operations inside an experiment ("at minute 10, fill 1 cm"), executed
// by the backend (padihat/water_events.py) with a camera photo before and after each one.
//
// Everything here is pure. The validation MIRRORS the backend's rules
// (padihat/water_event_rules.py, which stays the authority) so a bad plan is caught while the
// operator is still editing; the numbers (seconds per cm, the photo/settle allowance between events)
// come from the backend as `pump_config`, never hard-coded here.

import { DEFAULT_PUMP_CONFIG, formatPumpDuration, pumpDurationSeconds } from "utils/pumpFormat";

export const MAX_WATER_EVENTS = 24;
// Worst case the backend reserves around every operation: before-photo wait + settle + after-photo
// wait (60 + 30 + 60 s). Only a fallback for a backend that does not send it.
const DEFAULT_OVERHEAD_SECONDS = DEFAULT_PUMP_CONFIG.event_overhead_seconds;

export const emptyWaterEvent = () => ({ at_minute: "10", action: "fill", cm: "1" });

export const overheadSeconds = (config) => config?.event_overhead_seconds ?? DEFAULT_OVERHEAD_SECONDS;

const isBlank = (value) => value === "" || value === null || value === undefined;
const toNumber = (value) => (isBlank(value) ? NaN : Number(value));

// Rows hold whatever the inputs hold (strings while typing); the payload and the saved profile get
// real numbers.
export const normalizeWaterEvents = (events) =>
  (events || []).map((event) => ({
    at_minute: toNumber(event.at_minute),
    action: event.action,
    cm: toNumber(event.cm),
  }));

// A message for one row that is wrong on its own (no context needed), else null.
export const waterEventError = (event, config = DEFAULT_PUMP_CONFIG) => {
  const minute = toNumber(event.at_minute);
  if (Number.isNaN(minute) || minute < 0) return "Enter the minute (0 or more) the event starts at.";
  if (event.action !== "fill" && event.action !== "drain") return "Choose fill or drain.";
  const min = config?.min_cm_per_op ?? DEFAULT_PUMP_CONFIG.min_cm_per_op;
  const max = config?.max_cm_per_op ?? DEFAULT_PUMP_CONFIG.max_cm_per_op;
  const cm = toNumber(event.cm);
  if (Number.isNaN(cm) || cm < min - 1e-9 || cm > max + 1e-9) {
    return `Distance must be between ${min} and ${max} cm.`;
  }
  if (Math.abs(cm * 10 - Math.round(cm * 10)) > 1e-6) return "Distance must be a multiple of 0.1 cm.";
  return null;
};

// The tank level (cm) after each event, starting from the start level.
export const plannedLevels = (events, startLevelCm) => {
  let level = Number(startLevelCm);
  return normalizeWaterEvents(events).map((event) => {
    level += event.action === "fill" ? event.cm : -event.cm;
    return Math.round(level * 100) / 100;
  });
};

/**
 * Whole-plan validation, same rules as the backend:
 *  - each row is valid on its own, and rows are in strictly increasing time order
 *  - once there are events the tank setup (start level + limits) is required
 *  - an event may not start before the previous pump run + the photo/settle allowance is over
 *  - the last operation must finish within the run's planned length
 *  - the level may never leave the tank limits (the pump has no feedback, so relative events could
 *    otherwise add up to an overflow)
 * Returns { rowErrors: (string|null)[], setupError, firstError } - firstError is null when valid.
 */
export const validateWaterEvents = (
  events,
  { config = DEFAULT_PUMP_CONFIG, totalRunSeconds, startLevelCm, tankMinCm, tankMaxCm } = {}
) => {
  const list = events || [];
  const rowErrors = list.map(() => null);
  let setupError = null;
  if (list.length === 0) return { rowErrors, setupError, firstError: null };

  if (list.length > MAX_WATER_EVENTS) setupError = `Too many water events (max ${MAX_WATER_EVENTS}).`;

  const normalized = normalizeWaterEvents(list);
  normalized.forEach((event, index) => {
    rowErrors[index] = waterEventError(list[index], config);
    if (!rowErrors[index] && index > 0 && !rowErrors[index - 1] && event.at_minute <= normalized[index - 1].at_minute) {
      rowErrors[index] = "Events must be in strictly increasing time order.";
    }
  });

  const start = toNumber(startLevelCm);
  const min = toNumber(tankMinCm);
  const max = toNumber(tankMaxCm);
  if (!setupError) {
    if ([start, min, max].some(Number.isNaN)) {
      setupError = "Enter the tank's starting level and its minimum and maximum level.";
    } else if (!(min < max)) {
      setupError = "The tank minimum must be below its maximum.";
    } else if (start < min || start > max) {
      setupError = "The starting level must be within the tank limits.";
    }
  }

  const overhead = overheadSeconds(config);
  let previousEnd = null;
  normalized.forEach((event, index) => {
    if (rowErrors[index]) {
      previousEnd = null;
      return;
    }
    const begins = event.at_minute * 60;
    const duration = pumpDurationSeconds(event.cm, config);
    if (previousEnd !== null && begins < previousEnd + overhead) {
      const earliest = (previousEnd + overhead) / 60;
      rowErrors[index] = `Starts too soon after the previous event (earliest minute ${earliest.toFixed(1)}: pump time plus photo and settle allowance).`;
    } else if (totalRunSeconds > 0 && begins + duration > totalRunSeconds) {
      rowErrors[index] = "Would not finish within the run's planned length.";
    }
    previousEnd = begins + duration;
  });

  if (!setupError && !rowErrors.some(Boolean)) {
    plannedLevels(list, start).forEach((level, index) => {
      if (!rowErrors[index] && (level < min - 1e-9 || level > max + 1e-9)) {
        rowErrors[index] = `Would take the level to ${level} cm, outside the tank limits (${min} to ${max} cm).`;
      }
    });
  }

  const rowIndex = rowErrors.findIndex(Boolean);
  const firstError = setupError || (rowIndex >= 0 ? `Water event ${rowIndex + 1}: ${rowErrors[rowIndex]}` : null);
  return { rowErrors, setupError, firstError };
};

// "Minute 10: fill 1 cm (about 1 min 40 s of pumping)"
export const describeWaterEvent = (event, config = DEFAULT_PUMP_CONFIG) => {
  const cm = toNumber(event.cm);
  const minute = toNumber(event.at_minute);
  const pumping = Number.isNaN(cm) ? "" : ` (about ${formatPumpDuration(pumpDurationSeconds(cm, config))} of pumping)`;
  return `Minute ${minute}: ${event.action} ${cm} cm${pumping}`;
};

// The short change a planned event makes, for the label under its marker: "+1 cm" for a fill, "−0.5 cm"
// (a real minus sign) for a drain; "" when the size is not a number.
export const formatWaterEventDelta = (event) => {
  const cm = toNumber(event?.cm);
  if (Number.isNaN(cm)) return "";
  return `${event.action === "drain" ? "\u2212" : "+"}${cm} cm`;
};

// Where each planned event sits on a bar spanning the run's planned length, as a percentage from
// the left. Events past the end are clamped to 100 so they stay visible.
export const computeWaterEventMarkers = (events, totalSeconds) => {
  if (!(totalSeconds > 0)) return [];
  return (events || []).map((event, index) => {
    const at = toNumber(event.at_minute) * 60;
    const leftPct = Math.min(100, Math.max(0, (at / totalSeconds) * 100));
    return { index, leftPct, event };
  });
};

// ---- what a stored operation looks like to a person (the Pump operations panel) ----

const STATE_INFO = {
  scheduled: { label: "Waiting for its time", tone: "neutral" },
  capturing_before: { label: "Taking the before photo…", tone: "active" },
  starting_pump: { label: "Starting the pump…", tone: "active" },
  pumping: { label: "Pumping", tone: "active" },
  settling: { label: "Letting the water settle…", tone: "active" },
  capturing_after: { label: "Taking the after photo…", tone: "active" },
  done: { label: "Done", tone: "good" },
  skipped: { label: "Skipped - the pump did not run", tone: "warn" },
  failed: { label: "Failed", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export const waterEventStateInfo = (state) => STATE_INFO[state] || { label: state || "Unknown", tone: "neutral" };

const REASONS = {
  before_photo_timeout: "The camera did not confirm a before photo in time, so the pump was not started.",
  before_photo_request_failed: "The photo request could not be sent, so the pump was not started.",
  missed_window: "Its time passed while the server was busy or restarting, so it was skipped rather than run late.",
  controller_offline: "The greenhouse controller was offline.",
  firmware_too_old: "The controller firmware is too old to run timed pump operations.",
  pump_busy: "The pump was already running.",
  no_camera: "No ground-truth camera is linked to this controller.",
  run_ended: "The experiment run had already ended.",
  run_stopped: "The experiment run was stopped.",
  run_finished: "The experiment run finished.",
  operator_stop: "Stopped by an operator before it started.",
  superseded_by_new_run: "A new run started.",
  replaced_by_new_start: "The run was started again with a new plan.",
  publish_failed: "The pump command could not be sent.",
  pump_did_not_start: "The pump did not report starting; a stop was sent to be safe.",
  pump_stuck: "The pump did not stop when expected; a stop was sent.",
};

const WARNINGS = {
  no_camera: "No camera linked - this one ran without photos.",
  no_after_photo: "The after photo never arrived.",
  after_photo_failed: "The after photo failed.",
  after_photo_request_failed: "The after photo could not be requested.",
  before_photo_not_found: "The before photo was taken but not found.",
  after_photo_not_found: "The after photo was taken but not found.",
};

// Plain-language explanation for why an operation was skipped/failed ("" when there is none).
export const describeWaterEventReason = (reason) => {
  if (!reason) return "";
  if (REASONS[reason]) return REASONS[reason];
  if (reason.startsWith("before_photo_failed")) return "The camera could not take the before photo, so the pump was not started.";
  if (reason.startsWith("command_rejected")) return `The controller rejected the pump command (${reason.split(":")[1] || "unknown"}).`;
  return "";
};

// Warnings are free text joined with "; " (backend); explain the ones we know, pass the rest through.
export const describeWaterEventWarning = (warning) => {
  if (!warning) return [];
  return warning.split("; ").map((part) => {
    if (WARNINGS[part]) return WARNINGS[part];
    if (part.startsWith("pump stopped early")) return `The pump was stopped early (${part.replace("pump stopped early (", "").replace(")", "")}).`;
    return part;
  });
};

// ---- which operations need a person to look at them (the Pump card's flag) ----

// "bad" (red) for a failure, "warn" (amber) for a skip or a warning, null otherwise. A cancellation
// (the run stopped or ended, a new start replaced the plan) is routine, so it never flags.
export const operationReviewLevel = (event) => {
  if (!event || event.state === "cancelled") return null;
  if (event.state === "failed") return "bad";
  if (event.state === "skipped" || event.warning) return "warn";
  return null;
};

const toMs = (value) => (value ? new Date(value).getTime() : null);

/**
 * The operations that need review, and the loudest level among them: {level: "bad"|"warn"|null, items}.
 * Only the run on the chart counts - its run_id, or due inside its window (start..end, end null while
 * it runs), which also catches by-hand operations during it - so an old failure does not flag every
 * later run. With no run, the newest `fallbackCount` operations count. `events` are newest first.
 */
export const pumpOperationsNeedingReview = (events, { runId = null, start = null, end = null, fallbackCount = 10 } = {}) => {
  const list = events || [];
  const startMs = toMs(start);
  const endMs = toMs(end);
  const inRun = (event) => {
    if (runId && event.run_id === runId) return true;
    if (startMs === null) return false;
    const due = toMs(event.due_at);
    return due !== null && due >= startMs && (endMs === null || due <= endMs);
  };
  const scoped = runId || startMs !== null ? list.filter(inRun) : list.slice(0, fallbackCount);
  const items = scoped.filter((event) => operationReviewLevel(event) !== null);
  const level = items.some((event) => operationReviewLevel(event) === "bad") ? "bad" : items.length ? "warn" : null;
  return { level, items };
};
